import OpenAI from 'openai';

/**
 * OpenAI service for generating predictions with different response types
 */

/**
 * Generates predictions using OpenAI API
 * @param {string} question - The user's question
 * @param {string} responseType - Type of response (optimistic, pessimistic, etc.)
 * @param {string} apiKey - OpenAI API key
 * @param {number} count - Number of predictions to generate (default: 3)
 * @returns {Promise<string>} Raw response from OpenAI
 */
export const generatePredictions = async (question, responseType, apiKey, count = 3) => {
  if (!apiKey) {
    throw new Error('API key is required');
  }

  if (!question || question.trim().length === 0) {
    throw new Error('Question is required');
  }

  if (!responseType) {
    throw new Error('Response type is required');
  }

  // Initialize OpenAI client
  const openai = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Note: In production, API calls should go through a backend
  });

  // Create the prompt based on response type
  const prompt = createPrompt(question, responseType, count);

  // Create dynamic system prompt based on response type
  let systemPrompt;
  if (responseType.toLowerCase() === 'similar' || responseType.toLowerCase() === 'dissimilar') {
    systemPrompt = "You are a helpful assistant that generates items that are similar to or dissimilar from a given input. For 'similar' requests, focus on items that share common themes, categories, characteristics, or purposes. For 'dissimilar' requests, focus on items that are completely different, opposite, or unrelated. Always respond with exactly the requested number of items, each on a new line, without numbering.";
  } else {
    systemPrompt = "You are a helpful assistant that provides thoughtful predictions and insights. Always respond with exactly the requested number of predictions, each on a new line, numbered clearly.";
  }

  try {
    const completion = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: prompt
        }
      ],
      max_tokens: 1000,
      temperature: 0.7,
    });

    if (!completion.choices || completion.choices.length === 0) {
      throw new Error('No response received from OpenAI');
    }

    return completion.choices[0].message.content;
  } catch (error) {
    // Handle specific OpenAI errors
    if (error.status === 401) {
      throw new Error('Invalid API key. Please check your OpenAI API key.');
    } else if (error.status === 429) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    } else if (error.status === 403) {
      throw new Error('API access forbidden. Please check your API key permissions.');
    } else if (error.status === 500) {
      throw new Error('OpenAI server error. Please try again later.');
    } else if (error.code === 'insufficient_quota') {
      throw new Error('API quota exceeded. Please check your OpenAI billing.');
    } else if (error.name === 'TypeError' || error.message.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    } else {
      throw new Error(`API error: ${error.message || 'Unknown error occurred'}`);
    }
  }
};

/**
 * Creates a prompt based on the question and response type
 * @param {string} question - The user's question
 * @param {string} responseType - Type of response
 * @param {number} count - Number of predictions to generate
 * @returns {string} Formatted prompt
 */
const createPrompt = (question, responseType, count) => {
  let basePrompt;
  let specificInstructions = '';
  let formatInstructions;
  
  // Handle similar/dissimilar differently from predictions
  if (responseType.toLowerCase() === 'similar' || responseType.toLowerCase() === 'dissimilar') {
    if (responseType.toLowerCase() === 'similar') {
      basePrompt = `Please provide exactly ${count} things that are similar to: "${question}"`;
      specificInstructions = 'Focus on items that share common themes, categories, characteristics, purposes, or belong to the same domain. Make each item specific and clearly related to the input. Do not repeat the exact input or create variations of it.';
    } else {
      basePrompt = `Please provide exactly ${count} things that are dissimilar to: "${question}"`;
      specificInstructions = 'Focus on items that are from different categories, have opposite characteristics, serve different purposes, or belong to completely different domains. Make each item specific and clearly contrasting with the input.';
    }
    formatInstructions = `Please format your response as a simple list with exactly ${count} items, each on a separate line, without numbering. For example:
[First item]
[Second item]
[Third item]`;
  } else {
    basePrompt = `Please provide exactly ${count} ${responseType} predictions about: "${question}"`;
    
    switch (responseType.toLowerCase()) {
      case 'optimistic':
        specificInstructions = 'Focus on positive outcomes, opportunities, and best-case scenarios.';
        break;
      case 'pessimistic':
        specificInstructions = 'Focus on potential challenges, risks, and worst-case scenarios.';
        break;
      case 'realistic':
        specificInstructions = 'Focus on balanced, practical outcomes based on current trends and data.';
        break;
      case 'creative':
        specificInstructions = 'Focus on innovative, unconventional, and imaginative possibilities.';
        break;
      case 'analytical':
        specificInstructions = 'Focus on data-driven, logical, and systematic analysis.';
        break;
      case 'contrarian':
        specificInstructions = 'Focus on alternative viewpoints and counterintuitive outcomes.';
        break;
      default:
        specificInstructions = 'Provide thoughtful and insightful predictions.';
    }
    
    formatInstructions = `Please format your response as a numbered list with exactly ${count} predictions, each on a separate line. For example:
1. [First prediction]
2. [Second prediction]
3. [Third prediction]

Make each prediction specific, actionable, and relevant to the question.`;
  }

  return `${basePrompt}

${specificInstructions}

${formatInstructions}`;
};

/**
 * Validates API key format
 * @param {string} apiKey - API key to validate
 * @returns {boolean} True if valid format
 */
export const validateApiKeyFormat = (apiKey) => {
  if (!apiKey || typeof apiKey !== 'string') {
    return false;
  }
  
  // OpenAI API keys start with 'sk-' and are typically 51 characters long
  return apiKey.startsWith('sk-') && apiKey.length >= 20;
};

/**
 * Test API key by making a simple request
 * @param {string} apiKey - API key to test
 * @returns {Promise<boolean>} True if API key works
 */
export const testApiKey = async (apiKey) => {
  if (!validateApiKeyFormat(apiKey)) {
    throw new Error('Invalid API key format');
  }

  const openai = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  try {
    await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [{ role: "user", content: "Hello" }],
      max_tokens: 5,
    });
    return true;
  } catch (error) {
    if (error.status === 401) {
      throw new Error('Invalid API key');
    } else if (error.status === 403) {
      throw new Error('API key does not have required permissions');
    } else if (error.status === 429) {
      throw new Error('Rate limit exceeded');
    } else {
      throw new Error('API key test failed');
    }
  }
};

/**
 * Generates child predictions based on a parent prediction
 * @param {string} parentPredictionText - The parent prediction text
 * @param {string} responseType - Type of response (optimistic, pessimistic, etc.)
 * @param {string} apiKey - OpenAI API key
 * @param {number} count - Number of child predictions to generate (default: 3)
 * @returns {Promise<string>} Raw response from OpenAI
 */
export const generateChildPredictions = async (parentPredictionText, responseType, apiKey, count = 3) => {
  if (!apiKey) {
    throw new Error('API key is required');
  }

  if (!parentPredictionText || parentPredictionText.trim().length === 0) {
    throw new Error('Parent prediction text is required');
  }

  if (!responseType) {
    throw new Error('Response type is required');
  }

  // Initialize OpenAI client
  const openai = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Note: In production, API calls should go through a backend
  });

  // Create the prompt for child predictions based on response type
  const prompt = createChildPrompt(parentPredictionText, responseType, count);

  // Create dynamic system prompt based on response type
  let systemPrompt;
  if (responseType.toLowerCase() === 'similar' || responseType.toLowerCase() === 'dissimilar') {
    systemPrompt = "You are a helpful assistant that generates items that are similar to or dissimilar from a given input. For 'similar' requests, focus on items that share common themes, categories, characteristics, or purposes. For 'dissimilar' requests, focus on items that are completely different, opposite, or unrelated. Always respond with exactly the requested number of items, each on a new line, without numbering.";
  } else {
    systemPrompt = "You are a helpful assistant that provides thoughtful predictions and insights about what could happen next chronologically. Always respond with exactly the requested number of predictions, each on a new line, without numbering.";
  }

  try {
    const completion = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: prompt
        }
      ],
      max_tokens: 1000,
      temperature: 0.7,
    });

    if (!completion.choices || completion.choices.length === 0) {
      throw new Error('No response received from OpenAI');
    }

    return completion.choices[0].message.content;
  } catch (error) {
    // Handle specific OpenAI errors (same as generatePredictions)
    if (error.status === 401) {
      throw new Error('Invalid API key. Please check your OpenAI API key.');
    } else if (error.status === 429) {
      throw new Error('Rate limit exceeded. Please try again in a moment.');
    } else if (error.status === 403) {
      throw new Error('API access forbidden. Please check your API key permissions.');
    } else if (error.status === 500) {
      throw new Error('OpenAI server error. Please try again later.');
    } else if (error.code === 'insufficient_quota') {
      throw new Error('API quota exceeded. Please check your OpenAI billing.');
    } else if (error.name === 'TypeError' || error.message.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    } else {
      throw new Error(`API error: ${error.message || 'Unknown error occurred'}`);
    }
  }
};

/**
 * Creates a prompt for child predictions based on the parent prediction and response type
 * @param {string} parentPredictionText - The parent prediction text
 * @param {string} responseType - Type of response
 * @param {number} count - Number of predictions to generate
 * @returns {string} Formatted prompt for child predictions
 */
const createChildPrompt = (parentPredictionText, responseType, count) => {
  let specificInstructions = '';
  let finalInstructions = '';
  
  switch (responseType.toLowerCase()) {
    case 'optimistic':
      specificInstructions = `generate exactly ${count} optimistic predictions about what positive outcomes or opportunities could happen next chronologically`;
      finalInstructions = 'Make each prediction specific, actionable, and relevant to what would logically follow from the parent prediction.';
      break;
    case 'pessimistic':
      specificInstructions = `generate exactly ${count} pessimistic predictions about what negative outcomes or problems could happen next chronologically`;
      finalInstructions = 'Make each prediction specific, actionable, and relevant to what would logically follow from the parent prediction.';
      break;
    case 'general':
      specificInstructions = `generate exactly ${count} general predictions about what typical outcomes could happen next chronologically`;
      finalInstructions = 'Make each prediction specific, actionable, and relevant to what would logically follow from the parent prediction.';
      break;
    case 'similar':
      specificInstructions = `generate exactly ${count} things that are similar to this concept, idea, or situation in terms of theme, category, characteristics, or nature`;
      finalInstructions = 'Focus on items that share common attributes, belong to the same category, have similar purposes, or relate to the same domain. Make each item specific and clearly related to the input. Do not repeat the exact input or create variations of it.';
      break;
    case 'dissimilar':
      specificInstructions = `generate exactly ${count} things that are completely different, opposite, or unrelated to this concept, idea, or situation`;
      finalInstructions = 'Focus on items that are from different categories, have opposite characteristics, serve different purposes, or belong to completely different domains. Make each item specific and clearly contrasting with the input.';
      break;
    default:
      specificInstructions = `generate exactly ${count} predictions about what could happen next chronologically`;
      finalInstructions = 'Make each prediction specific, actionable, and relevant to what would logically follow from the parent prediction.';
  }

  if (responseType.toLowerCase() === 'similar' || responseType.toLowerCase() === 'dissimilar') {
    return `Given this input: "${parentPredictionText}", ${specificInstructions}. Return only the items, one per line, no numbering.

${finalInstructions}`;
  } else {
    return `Given that this prediction occurred: "${parentPredictionText}", ${specificInstructions}. Return only the predictions, one per line, no numbering.

${finalInstructions}`;
  }
};
